import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useDataSaver } from "@/context/DataSaverContext";
import api from "@/lib/api";
import { PushOptInBanner } from "@/components/PushOptInBanner";
import InboxDrawer from "@/components/InboxDrawer";
import { SoundChip } from "@/components/SoundChip";
import { House, PlusCircle, User as UserIcon, ShieldCheck, SignOut, Lightning, WhatsappLogo, DotsThree, Bell, Sun, Moon, WifiSlash, UsersThree, Tray, ChatCircleDots, Coins } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const signupChipCls = "inline-flex items-center px-3 py-0.5 rounded-full bg-gradient-to-r from-emerald-400 to-sky-500 text-black text-[12px] font-heading font-black shadow-[0_0_12px_-2px_rgba(52,211,153,0.8)]";

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { enabled: dataSaver, toggle: toggleDataSaver } = useDataSaver();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxOpen, setInboxOpen] = useState(false);

  // Poll unread notifications so the mobile bell shows a red dot in real-ish time.
  useEffect(() => {
    if (!user?.user_id) { setUnreadCount(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/notifications/unread-count");
        if (!cancelled) setUnreadCount(Number(r.data?.count || 0));
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.user_id]);

  // Top-nav primary items: Home, Find Friends, Post, Profile, Inbox.
  const navItems = [
    { to: "/feed",         label: "Home",         icon: House,       color: "text-sky-400",     testId: "nav-home" },
    { to: "/find-friends", label: "Find Friends", icon: UsersThree,  color: "text-fuchsia-400", testId: "nav-find-friends" },
    { to: "/post",         label: "Post",         icon: PlusCircle,  color: "text-emerald-400", testId: "nav-post", primary: true },
    { to: user?.user_id ? `/u/${user.user_id}` : "/feed", label: "Profile", icon: UserIcon, color: "text-cyan-300", testId: "nav-profile" },
    { onClick: () => setInboxOpen(true), label: "Inbox", icon: Tray, color: "text-amber-300", testId: "nav-inbox", showBadge: true },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-white/10 dark:border-white/10 p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
            <Lightning size={22} weight="fill" className="text-white" />
          </div>
          <span className="font-heading text-2xl font-black tracking-tight">Tain</span>
          {user ? (
            <NavLink
              to="/buy-points"
              data-testid="header-credits-counter-desktop"
              className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 ring-1 ring-yellow-400/30 text-[11px] font-heading font-bold text-yellow-300"
              aria-label="Credits balance"
            >
              <Coins size={11} weight="fill" />
              <span>{Number.isFinite(user?.points) ? user.points.toLocaleString() : "…"}</span>
            </NavLink>
          ) : (
            <NavLink to="/welcome" data-testid="header-signup-chip-desktop" className={`ml-auto ${signupChipCls}`}>Sign up</NavLink>
          )}
        </div>
        <div className="mt-2 h-6"><SoundChip testId="feed-sound-chip-desktop" /></div>
        <div className="h-8" />
        <nav className="flex flex-col gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const cls = ({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              } ${item.primary ? "font-black" : "font-medium"}`;
            if (item.onClick) {
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.onClick}
                  data-testid={item.testId + "-desktop"}
                  className="flex items-center gap-3 px-4 py-3 rounded-md text-white/60 hover:text-white hover:bg-white/5 transition-colors relative"
                >
                  <Icon size={20} weight="bold" className={item.color} />
                  <span className="font-medium">{item.label}</span>
                  {item.showBadge && unreadCount > 0 && (
                    <span className="ml-auto min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
              );
            }
            return (
              <NavLink
                key={item.to + item.label}
                to={item.to}
                data-testid={item.testId + "-desktop"}
                end={item.to === "/feed"}
                className={cls}
              >
                <Icon size={20} weight="bold" className={item.color} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          <NavLink
            to="/wallet"
            data-testid="nav-dashboard-desktop"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              }`
            }
          >
            <UserIcon size={20} weight="bold" className="text-lime-400" />
            <span className="font-medium">Wallet</span>
          </NavLink>
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin-desktop"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                  isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <ShieldCheck size={20} weight="bold" className="text-emerald-400" />
              <span className="font-medium">Admin</span>
            </NavLink>
          )}
        </nav>

        <div className="mt-auto space-y-2">
          <button
            data-testid="theme-toggle-desktop"
            onClick={toggleTheme}
            className="w-full flex items-center gap-2 px-4 py-3 rounded-md bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          >
            {theme === "dark" ? <Sun size={18} weight="fill" /> : <Moon size={18} weight="fill" />}
            <span className="text-sm font-semibold">
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </span>
          </button>
          <button
            data-testid="data-saver-toggle-desktop"
            onClick={toggleDataSaver}
            className={`w-full flex items-center gap-2 px-4 py-3 rounded-md transition-colors ${
              dataSaver ? "bg-yellow-500/15 text-yellow-300 ring-1 ring-yellow-400/30" : "bg-white/5 hover:bg-white/10 text-white/70 hover:text-white"
            }`}
          >
            <WifiSlash size={18} weight="bold" />
            <span className="text-sm font-semibold">
              Data Saver {dataSaver ? "\u2022 ON" : ""}
            </span>
          </button>
          <a
            data-testid="contact-admin-sidebar"
            href="https://wa.me/233539025750"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 rounded-md bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] transition-colors"
          >
            <WhatsappLogo size={20} weight="fill" />
            <span className="text-sm font-semibold">Contact Admin</span>
          </a>
          <NavLink
            to="/terms"
            data-testid="sidebar-terms-link"
            className="flex items-center gap-2 px-4 py-2 rounded-md text-white/50 hover:text-white/90 hover:bg-white/5 transition-colors text-xs"
          >
            Terms of Service
          </NavLink>
          <div className="flex items-center gap-3 px-2 pt-4 border-t border-white/10">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">${(user?.balance || 0).toFixed(2)}</p>
            </div>
            <button
              data-testid="logout-btn-desktop"
              onClick={logout}
              className="p-2 rounded-md hover:bg-white/10 text-white/70"
              title="Logout"
            >
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 pt-14 md:pt-0 md:pb-0">
        {/* Compact mobile top strip: logo + more menu */}
        <div className="md:hidden sticky top-0 z-30 flex justify-between items-center px-4 py-2 safe-pt bg-black/60 backdrop-blur-md border-b border-white/10">
          <div className="flex items-center gap-2">
            <NavLink to="/feed" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
                <Lightning size={16} weight="fill" className="text-white" />
              </div>
              <span className="font-heading text-lg font-black tracking-tight">Tain</span>
            </NavLink>
            {/* Credits counter sits beside the Tain logo so it never overlaps the feed avatar. */}
            {user ? (
              <NavLink
                to="/buy-points"
                data-testid="header-credits-counter"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 ring-1 ring-yellow-400/30 text-[11px] font-heading font-bold text-yellow-300"
                aria-label="Credits balance"
              >
                <Coins size={11} weight="fill" />
                <span data-testid="header-credits-value">{Number.isFinite(user?.points) ? user.points.toLocaleString() : "…"}</span>
              </NavLink>
            ) : (
              <NavLink to="/welcome" data-testid="header-signup-chip" className={signupChipCls}>Sign up</NavLink>
            )}
            <SoundChip />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="header-more-menu"
                className="p-2 rounded-md hover:bg-white/5 text-white/70 hover:text-white transition-colors"
              >
                <DotsThree size={22} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#121212] border-white/10 text-white w-56">
              <DropdownMenuItem data-testid="menu-wallet" onClick={() => navigate("/wallet")} className="cursor-pointer">
                <UserIcon size={16} weight="bold" className="mr-2" /> Wallet
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-theme" onClick={toggleTheme} className="cursor-pointer">
                {theme === "dark" ? <Sun size={16} weight="fill" className="mr-2" /> : <Moon size={16} weight="fill" className="mr-2" />}
                {theme === "dark" ? "Light mode" : "Dark mode"}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="menu-data-saver" onClick={toggleDataSaver} className="cursor-pointer">
                <WifiSlash size={16} weight="bold" className="mr-2" />
                Data Saver {dataSaver ? "\u2022 ON" : ""}
              </DropdownMenuItem>
              {user?.role === "admin" && (
                <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer">
                  <ShieldCheck size={16} weight="bold" className="mr-2" /> Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <a
                  data-testid="menu-contact-admin"
                  href="https://wa.me/233539025750"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center cursor-pointer"
                >
                  <WhatsappLogo size={16} weight="fill" className="mr-2 text-[#25D366]" /> Contact Admin
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                data-testid="menu-logout-mobile"
                onClick={logout}
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
              >
                <SignOut size={16} weight="bold" className="mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Outlet />
      </main>

      {/* Web-push opt-in banner — global, appears on any authed screen once
          per week until the user enables or explicitly dismisses. */}
      <PushOptInBanner user={user} />

      {/* Mobile compact TOP nav — colorful icons + tiny labels */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-40 bg-black/85 backdrop-blur-lg border-b border-white/10 safe-pt">
        <div className="flex justify-around items-stretch px-0.5 py-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            if (item.onClick) {
              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.onClick}
                  data-testid={item.testId + "-mobile"}
                  className="relative flex flex-col items-center justify-center gap-0.5 px-1.5 py-1 rounded-md min-w-[40px] flex-1"
                  title={item.label}
                >
                  <span className="relative">
                    <Icon size={20} weight="bold" className={`${item.color} opacity-90`} />
                    {item.showBadge && unreadCount > 0 && (
                      <span
                        data-testid="nav-inbox-badge-mobile"
                        className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500 ring-1 ring-black"
                      />
                    )}
                  </span>
                  <span className="text-[9px] leading-none font-heading font-bold tracking-tight text-white/80">{item.label}</span>
                </button>
              );
            }
            return (
              <NavLink
                key={item.to + item.label}
                to={item.to}
                end={item.to === "/feed"}
                data-testid={item.testId + "-mobile"}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-0.5 px-1.5 py-1 rounded-md min-w-[40px] flex-1 transition-colors ${
                    isActive ? "bg-white/10" : ""
                  } ${item.primary ? "text-emerald-400" : ""}`
                }
                title={item.label}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={item.primary ? 24 : 20}
                      weight={isActive || item.primary ? "fill" : "bold"}
                      className={isActive ? item.color : `${item.color} opacity-80`}
                    />
                    <span
                      className={`text-[9px] leading-none font-heading tracking-tight ${
                        item.primary ? "font-black" : "font-bold"
                      } ${isActive ? "text-white" : "text-white/70"}`}
                    >
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Inbox drawer — controlled by nav-inbox tap */}
      <InboxDrawer open={inboxOpen} onClose={() => setInboxOpen(false)} unreadCount={unreadCount} />
    </div>
  );
}
